import { useState } from 'react';

/**
 * Foto de uma usuária, ou as iniciais dela quando não há foto.
 *
 * Por ora aparece só a da própria usuária, no menu e em Meu perfil. Mora em
 * componente porque o responsável no Pipeline e na Visão 360 é o próximo lugar
 * natural para ela; lá serão centenas de fotos por tela, e isso pede uma rota
 * e um cuidado de carga que esta primeira versão não trouxe.
 */
export function Avatar({
  nome,
  foto,
  tamanho = 40,
  descricao,
}: {
  nome: string;
  /** Endereço da imagem, ou nulo para mostrar as iniciais. */
  foto: string | null;
  tamanho?: number;
  /**
   * Texto para leitor de tela. Sem ele o avatar é decorativo, que é o certo
   * quando o nome já aparece escrito ao lado.
   */
  descricao?: string;
}): React.JSX.Element {
  // Guarda qual endereço falhou, e não um sim ou não: uma foto nova precisa
  // de nova chance mesmo depois de a anterior ter falhado.
  const [falhou, definirFalhou] = useState<string | null>(null);
  const medidas = { width: tamanho, height: tamanho };

  if (foto && falhou !== foto) {
    return (
      <img
        src={foto}
        alt={descricao ?? ''}
        width={tamanho}
        height={tamanho}
        style={medidas}
        onError={() => definirFalhou(foto)}
        className="shrink-0 rounded-full border border-dourado/45 bg-marinho-suave object-cover"
      />
    );
  }

  return (
    <span
      {...(descricao ? { role: 'img', 'aria-label': descricao } : { 'aria-hidden': true })}
      style={{ ...medidas, fontSize: Math.round(tamanho * 0.38) }}
      className="flex shrink-0 select-none items-center justify-center rounded-full border border-dourado/45 bg-dourado-suave font-black text-marinho"
    >
      {iniciais(nome)}
    </span>
  );
}

/** Onde a própria foto é servida. A versão no endereço vence o cache do navegador. */
export function enderecoDaMinhaFoto(versaoDaFoto: string | null): string | null {
  return versaoDaFoto ? `/api/perfil/foto?v=${encodeURIComponent(versaoDaFoto)}` : null;
}

/** Primeira letra do primeiro e do último nome: "Maria da Silva" vira "MS". */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return (primeira + ultima).toLocaleUpperCase('pt-BR');
}
